import Joi from "joi";
import jwt_decode from "jwt-decode";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { VendorLogo, VendorLogoOrange } from "../../assets/VIP-ICON-SVG";
import { MainButton } from "../../components/Buttons";
import { MainInput } from "../../components/Inputs";
import { switchLang } from "../../helpers/lang";
import toastPopup from "../../helpers/toastPopup";
import branchServices from "../../services/branchServices";
import vendorSevices from "../../services/vendorServices";
import { authActions } from "../../store/auth-slice";
import "./Login.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ROUTES } from "constants";
import { roleService } from "services";

const initialUserState = { email: "", password: "" };
const formData = [
  { name: "email", type: "email", required: true },
  { name: "password", type: "password", required: true },
];

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [errorList, setErrorList] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [userType, setUserType] = useState("vendor");
  const [user, setUser] = useState(initialUserState);

  function changeLang(lang) {
    i18n.changeLanguage(lang);
    switchLang(lang);
  }

  function loginValidation(user) {
    const schema = Joi.object({
      email: Joi.string()
        .email({ minDomainSegments: 2, tlds: { allow: ["com", "net"] } })
        .required(),
      password: Joi.string()
        .pattern(
          /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/
        )
        .required(),
    });
    return schema.validate(user);
  }

  const vendorLogin = async () => (await vendorSevices.login(user)).data;
  const branchLogin = async () => (await branchServices.login(user)).data;

  const loginHandler = async (e) => {
    e.preventDefault();
    setErrorList([]);
    const validationResult = loginValidation(user);
    setLoading(true);
    if (validationResult.error) {
      setLoading(false);
      setErrorList(validationResult.error.details);
    } else {
      setLoading(true);
      try {
        const data = await roleService(userType).login(user);
        toastPopup.success(t("Success"));
        const tokenDecoded = jwt_decode(data.token);
        dispatch(
          authActions.login({
            token: data.token,
            vendorId:
              tokenDecoded.role === "vendor"
                ? tokenDecoded._id
                : tokenDecoded.vendor,
            branchId: tokenDecoded.role === "branch" && tokenDecoded._id,
            userRole: tokenDecoded.role,
            userData: data.record,
          })
        );
        navigate("/");
      } catch (e) {
        console.log(e);
        setErrorMessage(e?.response?.data?.error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="login">
      <div className="login-logo">
        <VendorLogo className="admin-login-logo" />
      </div>

      <div className="login-form">
        <div className="app-logo-small">
          <VendorLogoOrange className="login-logo-small" />
        </div>
        <ToggleLangBtn />

        <form className="login-box app-card-shadow" onSubmit={loginHandler}>
          <p>{t("login")}</p>

          {errorMessage ? <div className="err">{t(errorMessage)}</div> : ""}

          {errorList.map((error, index) => {
            if (error.message.includes("password")) {
              return (
                <div className="err" key={index}>
                  {t("WRONG PASSWORD")}{" "}
                  <i className="fa-solid fa-circle-xmark"></i>
                </div>
              );
            } else {
              return (
                <div key={index}>
                  <div className="err" key={index}>
                    {t("'email' must be a valid email")}{" "}
                    <i className="fa-solid fa-circle-xmark"></i>
                  </div>
                </div>
              );
            }
          })}

          {/* select role */}
          <div className="login-user-type">
            <p className="login-as">{t("login as")}</p>

            <div className="main-input-select">
              <div className="check-field">
                <div className="form-check">
                  <label className="form-check-label" htmlFor="userTypeVendor">
                    <input
                      hidden
                      type="radio"
                      name="userType"
                      id="userTypeVendor"
                      className="form-check-input"
                      checked={userType === "vendor"}
                      onChange={() => setUserType("vendor")}
                    />
                    <span className="form-check-circle"></span>
                    {t("vendor")}
                  </label>
                </div>
                <div className="form-check">
                  <label className="form-check-label" htmlFor="userTypeBranch">
                    <input
                      hidden
                      type="radio"
                      name="userType"
                      id="userTypeBranch"
                      className="form-check-input"
                      checked={userType === "branch"}
                      onChange={() => setUserType("branch")}
                    />
                    <span className="form-check-circle"></span>
                    {t("branch")}
                  </label>
                </div>

                <div className="form-check">
                  <label className="form-check-label" htmlFor="userTypecashier">
                    <input
                      hidden
                      type="radio"
                      name="userType"
                      id="userTypecashier"
                      className="form-check-input"
                      checked={userType === "cashier"}
                      onChange={() => setUserType("cashier")}
                    />
                    <span className="form-check-circle"></span>
                    {t("cashier")}
                  </label>
                </div>
              </div>
            </div>
          </div>

          {formData.map((formInput, index) => {
            return (
              <MainInput
                key={index}
                name={formInput.name}
                type={formInput.type}
                required={formInput.required}
                state={user}
                setState={setUser}
              />
            );
          })}
          <div className="text-primary hover:opacity-80">
            <Link to={`/${ROUTES.FORGOT_PASSWORD}`}>Forgot Password?</Link>
          </div>
          <div className="main-input-label">
            <MainButton
              type="submit"
              text={t("login")}
              loading={loading}
              className="w-full"
            />
          </div>
          <p className="!text-base">
            <span>{t("notRegistered")}</span>
            &nbsp;
            <Link to="/register" className="link-item">
              {t("CreateAccount")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}

const ToggleLangBtn = () => {
  const { i18n } = useTranslation();
  function changeLang(lang) {
    i18n.changeLanguage(lang);
    switchLang(lang);
  }
  return (
    <div className="lang">
      {localStorage.getItem("i18nextLng") === "en" ? (
        <button onClick={() => changeLang("ar")}>العربية</button>
      ) : (
        <button onClick={() => changeLang("en")}>English</button>
      )}
    </div>
  );
};
